import { jsx as _jsx } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { logger } from '@vista-digital/occ-domain';
import { observer } from 'mobx-react';
import { createRef, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { RecaptchaV2Size } from './types/size';
const isReadyFunction = () => typeof window !== 'undefined' &&
    typeof window.grecaptcha !== 'undefined' &&
    typeof window.grecaptcha.render === 'function';
const b = bemClassBuilder('v-captcha');
/**
 * Component that wraps a Google reCAPTCHA v2 element
 */
const RecaptchaV2Base = (props, ref) => {
    const { siteKey, className, onValueChanged, size, ietfLanguageTag } = props;
    const [readyCheckTimerId, setReadyCheckTimerId] = useState(0);
    const recaptchaRef = createRef();
    const [isReady, setIsReady] = useState(isReadyFunction());
    const [recaptchaId, setRecaptchaId] = useState('');
    const runCaptchaTest = useCallback(() => {
        if (isReady && recaptchaId !== undefined) {
            window.grecaptcha.execute(recaptchaId);
        }
    }, [isReady, recaptchaId]);
    const handleValueChanged = useCallback((captchaValue) => {
        onValueChanged(captchaValue);
    }, [onValueChanged]);
    const handleExpired = useCallback(() => {
        onValueChanged(undefined);
    }, [onValueChanged]);
    const updateReadyState = useCallback(() => {
        if (isReadyFunction()) {
            setIsReady(true);
            clearInterval(readyCheckTimerId);
        }
    }, [readyCheckTimerId]);
    const renderRecaptcha = useCallback(() => {
        const recaptchaElement = recaptchaRef.current;
        if (!recaptchaElement) {
            logger.error("Can't render Recaptcha on a null element");
            return;
        }
        // Return if the child element already exists
        if (recaptchaElement.firstChild)
            return;
        const wrapper = document.createElement('div');
        const newRecaptchaId = window.grecaptcha.render(wrapper, {
            sitekey: siteKey,
            hl: ietfLanguageTag,
            size: size,
            callback: handleValueChanged,
            'expired-callback': handleExpired
        });
        recaptchaElement.appendChild(wrapper);
        setRecaptchaId(newRecaptchaId);
        if (size === RecaptchaV2Size.Invisible)
            runCaptchaTest();
    }, [handleExpired, handleValueChanged, ietfLanguageTag, recaptchaRef, runCaptchaTest, siteKey, size]);
    useEffect(() => {
        if (isReady)
            renderRecaptcha();
        else
            setReadyCheckTimerId(window.setInterval(updateReadyState, 1000));
        return () => {
            clearInterval(readyCheckTimerId);
        };
    }, [isReady, readyCheckTimerId, renderRecaptcha, updateReadyState]);
    useImperativeHandle(ref, () => ({
        reset,
        runCaptchaTest
    }));
    const reset = () => {
        if (isReady && recaptchaId !== undefined) {
            window.grecaptcha.reset(recaptchaId);
            onValueChanged(undefined);
        }
    };
    return _jsx("div", { className: b.block(undefined, className), ref: recaptchaRef });
};
export const RecaptchaV2 = observer(forwardRef(RecaptchaV2Base));
RecaptchaV2.displayName = 'RecaptchaV2';
//# sourceMappingURL=recaptcha-v2.js.map